import { lazy, Suspense, useMemo } from 'react'

import { useTheme } from '@emotion/react'
import styled from '@emotion/styled'
import { useTranslation } from 'react-i18next'

import { ArcadeButton, Text, useArcadeModalActionOverride } from '@cash-design-system/react'
import {
  CardStudioBackgroundImage,
  CardStudioFullscreen,
  FloatingNavigationHeader,
} from '@cash-web/shared-card-studio-feature-components'
import { type BlockerComponentProps, useFlowContext } from '@cash-web/shared-flow-data-access-context'

const CardTagOrderConfirmationHero = lazy(() =>
  import('@cash-web/shared-card-studio-feature-main').then(module => ({
    default: module.CardTagOrderConfirmationHero,
  }))
)

const Content = styled.div`
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 100vh;
  min-height: 100dvh;
`

const BackgroundImage = styled(CardStudioBackgroundImage)`
  position: fixed;
`

const HeroContainer = styled.div`
  flex: 1 1 18rem;
  min-height: 15rem;
`

const BottomContent = styled.div`
  width: min(100%, 40rem);
  margin: 0 auto;
  padding: ${({ theme }) => theme.spacing.l}
    calc(env(safe-area-inset-right) + ${({ theme }) => theme.spacing.defaultContent})
    calc(env(safe-area-inset-bottom) + ${({ theme }) => theme.spacing.defaultContent})
    calc(env(safe-area-inset-left) + ${({ theme }) => theme.spacing.defaultContent});
`

const Copy = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.spacing.m};
  margin-bottom: ${({ theme }) => theme.spacing.l};
`

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.s};
`

export const BlockerCardTagOrderConfirmation = ({
  blocker,
  retreatAction,
}: BlockerComponentProps<'card_tag_order_confirmation_blocker'>) => {
  const { darkMode } = useTheme()
  const { t } = useTranslation()
  const flow = useFlowContext()

  const backgroundUrl = darkMode
    ? (blocker.background_image?.dark_url ?? blocker.background_image?.light_url)
    : blocker.background_image?.light_url
  const primaryButton = blocker.primary_button
  const primaryButtonAction = primaryButton?.action
  const secondaryButton = blocker.secondary_button
  const secondaryButtonAction = secondaryButton?.action
  const serverRetreatAction = retreatAction?.action
  const floatingHeader = useMemo(
    () => (
      <FloatingNavigationHeader
        leadingAction={
          serverRetreatAction
            ? {
                ariaLabel: t('Go back', {
                  context: 'aria label to go back to the last screen',
                }),
                disabled: flow.isLoading,
                icon: 'back',
                onClick: () => {
                  void flow.executeBlockerAction(serverRetreatAction)
                },
              }
            : undefined
        }
        title={blocker.toolbar_title}
      />
    ),
    [blocker.toolbar_title, flow, serverRetreatAction, t]
  )

  useArcadeModalActionOverride(
    useMemo(
      () => ({
        type: 'customHeader' as const,
        CustomHeader: floatingHeader,
      }),
      [floatingHeader]
    )
  )

  if (!blocker.card_theme_token || !blocker.tag_theme_token) {
    throw new Error('BlockerCardTagOrderConfirmation requires both card_theme_token and tag_theme_token')
  }

  return (
    <CardStudioFullscreen $scrollable={true}>
      {backgroundUrl ? <BackgroundImage alt="" aria-hidden={true} src={backgroundUrl} /> : null}
      <Content>
        <HeroContainer>
          <Suspense fallback={null}>
            <CardTagOrderConfirmationHero
              cameraDistance={10}
              cardThemeToken={blocker.card_theme_token}
              tagThemeToken={blocker.tag_theme_token}
            />
          </Suspense>
        </HeroContainer>
        <BottomContent>
          <Copy>
            <Text as="h2" color="textStandard" typography="headlineLarge">
              {blocker.title}
            </Text>
            {blocker.body ? (
              <Text as="p" color="textStandard" typography="bodyMedium">
                {blocker.body}
              </Text>
            ) : null}
          </Copy>
          <Actions>
            {secondaryButton?.text && secondaryButtonAction ? (
              <ArcadeButton
                onClick={() => {
                  void flow.executeBlockerAction(secondaryButtonAction)
                }}
                prominence="subtle"
                size="cta"
                type="button"
              >
                {secondaryButton.text}
              </ArcadeButton>
            ) : null}
            {primaryButton?.text && primaryButtonAction ? (
              <ArcadeButton
                disabled={flow.isLoading}
                isLoading={flow.isLoading}
                onClick={() => {
                  void flow.executeBlockerAction(primaryButtonAction)
                }}
                prominence="prominent"
                size="cta"
                type="button"
              >
                {primaryButton.text}
              </ArcadeButton>
            ) : null}
          </Actions>
        </BottomContent>
      </Content>
    </CardStudioFullscreen>
  )
}
